<template>
    <div class="assign-license-section">
        <!-- Show assignment UI when licenses are available -->
        <div v-if="hasAvailableLicenses" class="assign-license-content">
            <div class="assign-header">
                <div class="assign-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none">
                        <rect width="48" height="48" rx="24" fill="#E8F5E8"/>
                        <path d="M20 24L22.5 26.5L28 21M16 32H32C33.1046 32 34 31.1046 34 30V18C34 16.8954 33.1046 16 32 16H16C14.8954 16 14 16.8954 14 18V30C14 31.1046 14.8954 32 16 32Z" stroke="#22C55E" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </div>
                <h3 class="assign-title">Assign License</h3>
                <p class="assign-description">
                    Good news! You have <span class="highlight">{{ availableLicenses }} license{{ availableLicenses !== 1 ? 's' : '' }}</span> available to assign to {{dialogRef.data?.contact?.name}}.
                </p>
            </div>
            
            <div class="assign-details">
                <div class="detail-item">
                    <span class="detail-label">Plan:</span>
                    <span class="detail-value capitalize">{{ selectedPlanType }}</span>
                </div>
                <div class="detail-item">
                    <span class="detail-label">Term:</span>
                    <span class="detail-value">{{ selectedTerm }} months</span>
                </div>
            </div>
            
            <PrimeButton 
                label="Assign License" 
                variant="primary" 
                @click="assignLicense"
                :loading="isAssigning"
                class="assign-button"
                size="large"
            />
        </div>
        
        <!-- Show purchase UI when no licenses are available -->
        <div v-else class="purchase-license-content">
            <div class="purchase-header">
                <div class="purchase-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none">
                        <rect width="48" height="48" rx="24" fill="#FEF3C7"/>
                        <path d="M24 16V24L28 28M32 24C32 28.4183 28.4183 32 24 32C19.5817 32 16 28.4183 16 24C16 19.5817 19.5817 16 24 16Z" stroke="#F59E0B" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </div>
                <h3 class="purchase-title">Purchase License</h3>
                <p class="purchase-description">
                    You don't have any available licenses for this plan. Please purchase a license to assign to this contact.
                </p>
            </div>
            
            <div class="purchase-actions">
                <AddGiftCertificateBlock />
                    <div class="divider-div">
                        <div class="divider"></div>
                    </div>
                <PurchaseLicenseCostSummary />
                
                <PurchaseLicensesConfirmationBlock />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { getDialogRef } from '~/composables/dynamic-dialog';
import { useSubscribeStore } from '~/stores/components/modals/subscription-modal';
import { usePersonStore } from '~/stores/person';
import { useContactsStore } from '~/stores/person/contacts';
import { PurchaseType } from '~/types/licenses/licenses';
import { ModalNames } from '~/types/modals';
import AddGiftCertificateBlock from './AddGiftCertificateBlock.vue';
import PurchaseLicenseCostSummary from './PurchaseLicenseCostSummary.vue';
import PurchaseLicensesConfirmationBlock from './PurchaseLicensesConfirmationBlock.vue';

const dialogRef = getDialogRef(ModalNames.PURCHASE_LICENSES);
const personStore = usePersonStore();
const subscribeStore = useSubscribeStore();
const { showSuccess, showError, showWarning } = useGlobalToast();
const isAssigning = ref(false);
const contactsStore = useContactsStore();

const { selectedPlanType, selectedTerm, purchaseType } = storeToRefs(subscribeStore);

// Check if licenses are available for the selected plan and term
const hasAvailableLicenses = computed(() => {
    const availableLicense = personStore.person?.licenses.find(license => {
        return license.slug === selectedPlanType.value && 
               license.term === selectedTerm.value && 
               license.expired === false && 
               !license.assigned_to;
    });
    
    return !!availableLicense;
});

// Count available licenses for display
const availableLicenses = computed(() => {
    if (purchaseType.value !== PurchaseType.GIFT) return 0;
    
    const availableLicenses = personStore.person?.licenses.filter(license => {
        return license.slug === selectedPlanType.value && 
               license.term === selectedTerm.value && 
               license.expired === false && 
               !license.assigned_to;
    });
    
    return availableLicenses?.length || 0;
});



const assignLicense = async () => {
    try {
        isAssigning.value = true;
        
        const contact = dialogRef.value?.data?.contact;
        if (!contact) {
            showError('Error', 'Contact information not found');
            return;
        }

        // Find the available license
        const availableLicense = personStore.person?.licenses.find(license => {
            return license.slug === selectedPlanType.value && 
                   license.term === selectedTerm.value && 
                   license.expired === false && 
                   !license.assigned_to;
        });

        if (!availableLicense) {
            showError('Error', 'No available license found');
            return;
        }

        // Use the existing assignLicense method from the store
        await subscribeStore.assignLicense(availableLicense.id, contact);
        await contactsStore.fetchContacts();

        dialogRef.value?.close();
    } catch (err) {
        showError('Error', 'Failed to assign license');
    } finally {
        isAssigning.value = false;
    }
};
</script>

<style lang="scss" scoped>
.assign-license-section {
    @apply flex flex-col items-center justify-center p-4;
}

.assign-license-content {
    @apply text-center max-w-md;

    .assign-header {
        @apply flex flex-col items-center mb-6;

        .assign-icon {
            @apply mb-4;
        }

        .assign-title {
            @apply font-inter text-xl font-semibold leading-6 mb-3;
        }

        .assign-description {
            @apply font-inter text-sm font-normal leading-5 text-zinc-600 mb-6;

            .highlight {
                @apply font-semibold text-green-600;
            }
        }
    }

    .assign-details {
        @apply flex flex-col gap-2 mb-6 p-4 bg-zinc-50 rounded-lg border border-zinc-200;

        .detail-item {
            @apply flex justify-between items-center;

            .detail-label {
                @apply font-inter text-sm font-normal leading-5 text-zinc-600;
            }

            .detail-value {
                @apply font-inter text-sm font-semibold leading-5 text-zinc-900;
            }
        }
    }

    .assign-button {
        @apply w-full;
    }
}

.purchase-license-content {
    @apply w-full;

    .purchase-header {
        @apply flex flex-col items-center mb-6;

        .purchase-icon {
            @apply mb-4;
        }

        .purchase-title {
            @apply font-inter text-xl font-semibold leading-6 mb-3 text-center;
        }

        .purchase-description {
            @apply font-inter text-sm font-normal leading-5 text-zinc-600 mb-6 text-center;
        }
    }

    .purchase-actions {
        @apply w-full;
    }
}

.divider-div {
    @apply border border-zinc-300 w-full border-t my-6;
}

.divider {
    @apply h-px self-stretch bg-zinc-300;
}
</style> 